import { useCallback, useEffect, useMemo, useState } from "react";
import {
  DirectionsRenderer,
  DirectionsService,
  GoogleMap,
  InfoWindowF,
  MarkerF,
  useJsApiLoader,
} from "@react-google-maps/api";
import { Col, Row } from "react-bootstrap";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Breadcrumbs from "../../../components/Breadcrumbs";
import CustomModal from "../../../components/CustomModal";
import Input from "../../../components/input";
import LogsComments from "../../../components/LogsComments";
import UserAvatar from "../../../components/UserAvatar";
import receiptPreviewImage from "../../../assets/images/refuel_img.png";
import { apiService } from "../../../services/apiService";
import "../../../styles/Access.css";
import "../../../styles/DriverExpense.css";
import "../../../styles/DriverPayments.css";
import "../../../styles/DriverSafety.css";
import "../../../styles/Input.css";
import "../../../styles/Issue.css";
import "../../../styles/Table.css";
import "../../../styles/Trip.css";
import "../../../styles/TripCostEstimation.css";
import SpinnerLoader from "../../../utils/SpinnerLoader";
import { errorNotify } from "../../../utils/toast";

const DETAIL_TABS = [
  { key: "overview", label: "Overview" },
  { key: "expense-log", label: "Expense Log" },
  { key: "trip-context", label: "Trip Context" },
];

const MAP_CONTAINER_STYLE = {
  width: "100%",
  height: "420px",
};

const MAP_OPTIONS = {
  zoomControl: true,
  mapTypeControl: false,
  scaleControl: false,
  streetViewControl: false,
  rotateControl: false,
  fullscreenControl: false,
  styles: [
    {
      featureType: "poi",
      stylers: [{ visibility: "off" }],
    },
  ],
};

const safeArray = (value) => (Array.isArray(value) ? value : []);

const toNumber = (value, fallback = 0) => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : fallback;
};

const formatAmount = (value) =>
  new Intl.NumberFormat("en-PK", {
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const formatDetailDate = (value = "") => {
  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) return "-";

  return parsedDate
    .toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    })
    .replace(/ /g, "-");
};

const formatDetailDateTime = (value = "") => {
  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) return "-";

  return parsedDate
    .toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
    .replace(",", "");
};

const humanizeText = (value = "") => {
  const normalizedValue = String(value || "").trim();
  if (!normalizedValue) return "-";

  return normalizedValue
    .replace(/[_-]+/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
};

const buildPreviewAttachment = (file, fallbackKey) => {
  const filePath = file?.file_path || file?.path || "";
  const fileName = filePath ? filePath.split("/").pop() : `attachment-${fallbackKey}`;

  return {
    id: file?.id || `${fallbackKey}-${fileName}`,
    name: fileName,
    preview: filePath
      ? `${process.env.REACT_APP_API_FILES_URL}${filePath}`
      : receiptPreviewImage,
  };
};

const renderFieldLabel = (label, required = false) => (
  <span className="assets_vehicle_field_label">
    <span>{label}</span>
    {required ? <span className="assets_vehicle_field_required">*</span> : null}
  </span>
);

const toPoint = (lat, lng) => {
  const resolvedLat = Number(lat);
  const resolvedLng = Number(lng);

  if (!Number.isFinite(resolvedLat) || !Number.isFinite(resolvedLng)) {
    return null;
  }

  return { lat: resolvedLat, lng: resolvedLng };
};

const buildFallbackRecord = (row = {}) => {
  if (!row || typeof row !== "object") return null;

  return {
    id: Number(row?.tripRecordId || 0) || null,
    tripId: row?.tripId || "-",
    vehicleId: row?.vehicleId || "-",
    vehicleOdometer: "",
    customerName: row?.customerName || "-",
    driverName: row?.driverName || "-",
    driverEmail: "",
    driverImage: row?.driverImage || "",
    totalExpense: toNumber(row?.totalAmount),
    expenseLogs: safeArray(row?.logs).map((log, index) => ({
      id: log?.id || `fallback-log-${index + 1}`,
      category: log?.category || "-",
      expenseDateDisplay: log?.expenseDateDisplay || "-",
      expenseDateValue: log?.expenseDateValue || "",
      totalAmount: toNumber(log?.totalAmount),
      location: "-",
      lat: null,
      lng: null,
      attachments: safeArray(log?.attachments),
    })),
    rawData: null,
  };
};

const normalizeDriverExpenseDetail = (payload = {}, fallbackRecord = null) => {
  const source = payload?.data || payload || {};
  const fallback = fallbackRecord || {};
  const tripExpenses = safeArray(source?.trip_expenses)
    .map((expense, index) => ({
      id: expense?.id || `trip-expense-${index + 1}`,
      category:
        expense?.expense_category?.title || humanizeText(expense?.trip_expense_category),
      expenseDateDisplay: formatDetailDate(expense?.created_at),
      expenseDateValue: expense?.created_at || "",
      totalAmount: toNumber(expense?.amount),
      location: expense?.location || "-",
      lat: expense?.lat,
      lng: expense?.lng,
      attachments: safeArray(expense?.files).map((file, fileIndex) =>
        buildPreviewAttachment(file, `${expense?.id || "trip-expense"}-${fileIndex + 1}`)
      ),
    }))
    .sort(
      (firstItem, secondItem) =>
        new Date(secondItem.expenseDateValue || 0).getTime() -
        new Date(firstItem.expenseDateValue || 0).getTime()
    );

  return {
    id: Number(source?.id || fallback?.id || 0) || null,
    tripId: source?.trip_id || fallback?.tripId || "-",
    vehicleId:
      source?.vehicle?.registration_number ||
      source?.vehicle?.vehicle_id ||
      fallback?.vehicleId ||
      "-",
    vehicleOdometer: String(source?.vehicle?.odm || fallback?.vehicleOdometer || "").trim(),
    customerName: source?.customer?.name || fallback?.customerName || "-",
    driverName: source?.primary_driver?.username || fallback?.driverName || "-",
    driverEmail: source?.primary_driver?.email || fallback?.driverEmail || "",
    driverImage: source?.primary_driver?.image || fallback?.driverImage || "",
    totalExpense: toNumber(source?.total_expense ?? fallback?.totalExpense),
    expenseLogs: tripExpenses.length ? tripExpenses : safeArray(fallback?.expenseLogs),
    rawData: source,
  };
};

const buildHistoryItems = (response = {}) => {
  const responseData = response?.data || response || [];
  if (Array.isArray(responseData)) return responseData;
  if (Array.isArray(responseData?.results)) return responseData.results;
  if (Array.isArray(responseData?.history)) return responseData.history;
  if (Array.isArray(response?.results)) return response.results;
  return [];
};

const DriverExpenseTripContextMap = ({ startPoint, endPoint, markers = [] }) => {
  const hasApiKey = Boolean((process.env.REACT_APP_API_GOOGLE_MAP_KEY || "").trim());
  const [hoveredMarkerId, setHoveredMarkerId] = useState(null);
  const [directionsResponse, setDirectionsResponse] = useState(null);
  const [mapCenter, setMapCenter] = useState(startPoint || markers[0] || { lat: 30.3753, lng: 69.3451 });
  const mapZoom = startPoint && endPoint ? 6 : 5;

  const { isLoaded } = useJsApiLoader({
    id: "driver-expense-trip-context-map",
    googleMapsApiKey: (process.env.REACT_APP_API_GOOGLE_MAP_KEY || "").trim(),
  });

  useEffect(() => {
    setDirectionsResponse(null);
  }, [endPoint, startPoint]);

  useEffect(() => {
    setMapCenter(startPoint || markers[0] || { lat: 30.3753, lng: 69.3451 });
  }, [markers, startPoint]);

  if (!hasApiKey) {
    return (
      <div className="driver_expense_context_empty">
        Google Maps API key is missing for trip context preview.
      </div>
    );
  }

  if (!startPoint && !endPoint && !safeArray(markers).length) {
    return (
      <div className="driver_expense_context_empty">
        No trip coordinates are available for this expense record.
      </div>
    );
  }

  if (!isLoaded) {
    return <div className="driver_expense_context_empty">Loading trip map...</div>;
  }

  return (
    <div className="driver_expense_context_map">
      <GoogleMap
        mapContainerStyle={MAP_CONTAINER_STYLE}
        center={mapCenter}
        zoom={mapZoom}
        options={MAP_OPTIONS}
      >
        {startPoint && endPoint && !directionsResponse ? (
          <DirectionsService
            options={{
              origin: startPoint,
              destination: endPoint,
              travelMode: "DRIVING",
            }}
            callback={(result, status) => {
              if (result && status === "OK") {
                setDirectionsResponse(result);
              }
            }}
          />
        ) : null}

        {directionsResponse ? (
          <DirectionsRenderer
            options={{
              directions: directionsResponse,
              suppressMarkers: true,
              preserveViewport: false,
              polylineOptions: {
                strokeColor: "#2563EB",
                strokeOpacity: 0.85,
                strokeWeight: 4,
              },
            }}
          />
        ) : null}

        {startPoint ? (
          <MarkerF
            position={startPoint}
            label={{ text: "S", color: "#ffffff", fontWeight: "700" }}
          />
        ) : null}

        {endPoint ? (
          <MarkerF
            position={endPoint}
            label={{ text: "E", color: "#ffffff", fontWeight: "700" }}
          />
        ) : null}

        {safeArray(markers).map((marker, index) => (
          <MarkerF
            key={marker.id || `marker-${index + 1}`}
            position={marker.position}
            label={{
              text: String(index + 1),
              color: "#ffffff",
              fontWeight: "700",
            }}
            onMouseOver={() => setHoveredMarkerId(marker.id || `marker-${index + 1}`)}
            onMouseOut={() => setHoveredMarkerId(null)}
          >
            {hoveredMarkerId === (marker.id || `marker-${index + 1}`) ? (
              <InfoWindowF onCloseClick={() => setHoveredMarkerId(null)}>
                <div className="driver_expense_map_card">
                  <strong>{marker.title}</strong>
                  <span>{marker.subtitle}</span>
                  <span>{marker.amount}</span>
                </div>
              </InfoWindowF>
            ) : null}
          </MarkerF>
        ))}
      </GoogleMap>
    </div>
  );
};

const DriverExpenseDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const [activeTab, setActiveTab] = useState("overview");
  const [record, setRecord] = useState(() => buildFallbackRecord(location.state?.record));
  const [tripContext, setTripContext] = useState(null);
  const [loading, setLoading] = useState(true);
  const [historyItems, setHistoryItems] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [previewAttachment, setPreviewAttachment] = useState(null);

  const fetchExpenseDetail = useCallback(async () => {
    const fallbackRecord = buildFallbackRecord(location.state?.record);
    setLoading(true);

    try {
      const detailResponse = await apiService.getDriverExpenseDetail(id);
      const detailRecord = normalizeDriverExpenseDetail(detailResponse, fallbackRecord);
      setRecord(detailRecord);

      if (detailRecord?.id) {
        try {
          const tripResponse = await apiService.getTripDetails(detailRecord.id);
          setTripContext(tripResponse?.data || tripResponse || null);
        } catch (tripError) {
          setTripContext(null);
          errorNotify(tripError || "Failed to fetch trip context");
        }
      } else {
        setTripContext(null);
      }
    } catch (error) {
      setTripContext(null);
      setRecord(fallbackRecord);
      errorNotify(error || "Failed to fetch expense detail");
    } finally {
      setLoading(false);
    }
  }, [id, location.state?.record]);

  useEffect(() => {
    fetchExpenseDetail();
  }, [fetchExpenseDetail]);

  const tripContextId = useMemo(
    () => Number(record?.id || id || 0) || null,
    [id, record?.id]
  );

  const fetchHistory = useCallback(
    async (historyType = "all") => {
      if (!tripContextId) {
        setHistoryItems([]);
        return;
      }

      setHistoryLoading(true);

      try {
        const response = await apiService.getTripHistory({
          history_type: historyType,
          trip_id: tripContextId,
        });
        setHistoryItems(buildHistoryItems(response));
      } catch (error) {
        setHistoryItems([]);
        errorNotify(error || "Failed to fetch trip history");
      } finally {
        setHistoryLoading(false);
      }
    },
    [tripContextId]
  );

  const submitComment = useCallback(
    async (payload = {}) => {
      if (!tripContextId) {
        throw new Error("Trip reference is unavailable for this expense.");
      }

      await apiService.commentOnTrip({
        ...payload,
        trip_id: tripContextId,
      });
    },
    [tripContextId]
  );

  const overviewFields = useMemo(
    () => [
      { id: "driver", label: "Driver", value: record?.driverName || "-" },
      { id: "trip-id", label: "Trip ID", value: record?.tripId || "-" },
      { id: "vehicle", label: "Vehicle", value: record?.vehicleId || "-" },
      { id: "customer", label: "Customer", value: record?.customerName || "-" },
      {
        id: "driver-email",
        label: "Driver Email",
        value: record?.driverEmail || "-",
      },
      {
        id: "vehicle-odometer",
        label: "Vehicle Odometer",
        value: record?.vehicleOdometer || "-",
      },
    ],
    [record]
  );

  const attachmentCount = useMemo(
    () =>
      safeArray(record?.expenseLogs).reduce(
        (sum, log) => sum + safeArray(log?.attachments).length,
        0
      ),
    [record?.expenseLogs]
  );

  const routeLabel = useMemo(() => {
    if (tripContext?.dispatch_point && tripContext?.decanting_point) {
      return `${tripContext.dispatch_point} -> ${tripContext.decanting_point}`;
    }

    if (tripContext?.route) return String(tripContext.route).trim();
    return record?.tripId || "-";
  }, [record?.tripId, tripContext]);

  const startPoint = useMemo(
    () =>
      toPoint(
        tripContext?.dispatch_latitude,
        tripContext?.dispatch_longitude
      ),
    [tripContext?.dispatch_latitude, tripContext?.dispatch_longitude]
  );

  const endPoint = useMemo(
    () =>
      toPoint(
        tripContext?.decanting_latitude,
        tripContext?.decanting_longitude
      ),
    [tripContext?.decanting_latitude, tripContext?.decanting_longitude]
  );

  const expenseMarkers = useMemo(
    () =>
      safeArray(record?.expenseLogs)
        .map((log) => {
          const position = toPoint(log?.lat, log?.lng);
          if (!position) return null;

          return {
            id: log.id,
            position,
            title: log.category || "Expense Log",
            subtitle: log.location || "On-route expense",
            amount: `PKR ${formatAmount(log.totalAmount)}`,
          };
        })
        .filter(Boolean),
    [record?.expenseLogs]
  );

  const tripContextFields = useMemo(
    () => [
      { id: "route", label: "Route", value: routeLabel || "-" },
      {
        id: "dispatch",
        label: "Dispatch Location",
        value: tripContext?.dispatch_point || "-",
      },
      {
        id: "decanting",
        label: "Decanting Location",
        value: tripContext?.decanting_point || "-",
      },
      {
        id: "trip-status",
        label: "Trip Status",
        value: humanizeText(tripContext?.status),
      },
      {
        id: "start-date",
        label: "Start Date",
        value: formatDetailDateTime(tripContext?.start_at),
      },
      {
        id: "expected-end",
        label: "Expected End",
        value: formatDetailDateTime(tripContext?.expected_end_at),
      },
    ],
    [routeLabel, tripContext]
  );

  const generalNotes = useMemo(() => {
    const notes = [
      `Customer: ${record?.customerName || "-"}`,
      `Vehicle Registration: ${record?.vehicleId || "-"}`,
      `Vehicle Odometer: ${record?.vehicleOdometer || "-"}`,
      `Primary Driver Email: ${record?.driverEmail || "-"}`,
      `Recorded Expense Items: ${safeArray(record?.expenseLogs).length}`,
      `Total Attachments: ${attachmentCount}`,
    ];

    if (tripContext?.description) {
      notes.push(`Trip Description: ${tripContext.description}`);
    }

    if (tripContext?.note) {
      notes.push(`Trip Note: ${tripContext.note}`);
    }

    return notes;
  }, [attachmentCount, record, tripContext]);

  if (loading && !record) {
    return <SpinnerLoader />;
  }

  if (!record) {
    return (
      <div className="driver_expense_page trip_cost_estimation_page driver_expense_details_page">
        <Breadcrumbs values={["Finance", "Driver Expense Approvals", "Expense Detail"]} />
        <div className="page_container">
          <div className="table_no_records">Expense record not found</div>
        </div>
      </div>
    );
  }

  return (
    <div className="driver_expense_page trip_cost_estimation_page driver_expense_details_page">
      <Breadcrumbs values={["Finance", "Driver Expense Approvals", "Expense Detail"]} />

      <div className="page_header access_view">
        <h2>Expense Detail</h2>

        <div className="driver_payment_header_actions">
          <button type="button" className="cancel_btn" onClick={() => navigate("/expenses")}>
            Cancel
          </button>
        </div>
      </div>

      <div className="page_container">
        <Row className="driver_page_layout trip_cost_estimation_details_layout">
          <Col lg={9}>
            <div className="driver_payment_detail_bar driver_expense_detail_bar">
              <span>Expense ID:</span>
              <strong>{record.tripId}</strong>
            </div>

            <div className="driver_payment_hero driver_expense_hero">
              <div className="driver_payment_hero_identity">
                <UserAvatar
                  username={record.driverName}
                  image={record.driverImage}
                  size={58}
                  fontSize="18px"
                  marginRight="0px"
                />
                <div>
                  <h3>{record.driverName}</h3>
                  <p>{record.customerName}</p>
                </div>
              </div>

              <div className="driver_payment_hero_summary">
                <div>
                  <h4>Total Expense (PKR)</h4>
                  <p>{`${safeArray(record.expenseLogs).length} expense log item(s)`}</p>
                </div>
                <strong>{formatAmount(record.totalExpense)}</strong>
              </div>
            </div>

            <div className="driver_payment_tab_row">
              {DETAIL_TABS.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  className={activeTab === tab.key ? "is_active" : ""}
                  onClick={() => setActiveTab(tab.key)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {activeTab === "overview" ? (
              <div className="trip_information_main_div driver_expense_details_shell">
                <div className="driver_form_content">
                  <div className="driver_form_section">
                    <div className="driver_form_section_header">
                      <h3>Expense Information</h3>
                      <p>Main information for this expense record.</p>
                    </div>

                    <Row>
                      {overviewFields.map((field) => (
                        <Col md={6} key={field.id}>
                          <Input
                            label={renderFieldLabel(field.label)}
                            type="text"
                            name={field.id}
                            value={field.value}
                            onChange={() => {}}
                            disabled
                            required={false}
                          />
                        </Col>
                      ))}

                      <Col md={6}>
                        <Input
                          label={renderFieldLabel("Total Expense Amount")}
                          type="number"
                          name="total-expense-amount"
                          value={record.totalExpense}
                          onChange={() => {}}
                          showExtraText
                          extraText="PKR"
                          disabled
                          required={false}
                        />
                      </Col>

                      <Col md={6}>
                        <Input
                          label={renderFieldLabel("Linked Attachments")}
                          type="text"
                          name="linked-attachments"
                          value={attachmentCount}
                          onChange={() => {}}
                          disabled
                          required={false}
                        />
                      </Col>
                    </Row>
                  </div>
                </div>
              </div>
            ) : null}

            {activeTab === "expense-log" ? (
              <div className="trip_information_main_div driver_expense_details_shell">
                <div className="driver_form_content">
                  <div className="driver_form_section">
                    <div className="driver_form_section_header">
                      <h3>Expense Log Information</h3>
                      <p>All trip expense entries linked with this approval.</p>
                    </div>

                    {safeArray(record.expenseLogs).length ? (
                      <div className="driver_expense_log_cards">
                        {record.expenseLogs.map((log) => (
                          <div key={log.id} className="driver_expense_log_detail_card">
                            <Row>
                              <Col md={4}>
                                <Input
                                  label={renderFieldLabel("Category")}
                                  type="text"
                                  name={`log-category-${log.id}`}
                                  value={log.category}
                                  onChange={() => {}}
                                  disabled
                                  required={false}
                                />
                              </Col>
                              <Col md={4}>
                                <Input
                                  label={renderFieldLabel("Expense Date")}
                                  type="text"
                                  name={`log-date-${log.id}`}
                                  value={log.expenseDateDisplay}
                                  onChange={() => {}}
                                  disabled
                                  required={false}
                                />
                              </Col>
                              <Col md={4}>
                                <Input
                                  label={renderFieldLabel("Amount")}
                                  type="number"
                                  name={`log-amount-${log.id}`}
                                  value={log.totalAmount}
                                  onChange={() => {}}
                                  showExtraText
                                  extraText="PKR"
                                  disabled
                                  required={false}
                                />
                              </Col>
                            </Row>

                            {safeArray(log.attachments).length ? (
                              <div className="driver_expense_attachment_strip">
                                {log.attachments.map((attachment) => (
                                  <button
                                    type="button"
                                    key={attachment.id}
                                    className="driver_expense_attachment_thumbnail"
                                    title={attachment.name}
                                    onClick={() => setPreviewAttachment(attachment)}
                                  >
                                    <img
                                      src={attachment.preview}
                                      alt={attachment.name || "Expense attachment"}
                                    />
                                  </button>
                                ))}
                              </div>
                            ) : (
                              <div className="driver_expense_inline_empty">
                                No attachments linked to this expense log.
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="table_no_records">No expense logs found for this record</div>
                    )}
                  </div>
                </div>
              </div>
            ) : null}

            {activeTab === "trip-context" ? (
              <div className="trip_information_main_div driver_expense_details_shell">
                <div className="driver_form_content">
                  <div className="driver_form_section">
                    <div className="driver_form_section_header driver_expense_context_header">
                      <div>
                        <h3>Trip Information</h3>
                        <p>Context from the linked trip record.</p>
                      </div>
                    </div>

                    <Row>
                      {tripContextFields.map((field) => (
                        <Col md={6} key={field.id}>
                          <Input
                            label={renderFieldLabel(field.label)}
                            type="text"
                            name={field.id}
                            value={field.value}
                            onChange={() => {}}
                            disabled
                            required={false}
                          />
                        </Col>
                      ))}
                    </Row>

                    <DriverExpenseTripContextMap
                      startPoint={startPoint}
                      endPoint={endPoint}
                      markers={expenseMarkers}
                    />
                  </div>
                </div>
              </div>
            ) : null}
          </Col>

          <Col lg={3}>
            <div className="service_cost_summary_details trip_cost_estimation_details_sidebar driver_details_sidebar driver_expense_details_sidebar">
              <div className="service_cost_summary mb-3">
                <h6>Expense Summary</h6>
                <p>Quick metrics for this trip expense.</p>

                <div className="service_item">
                  <div>Total Expense (PKR)</div>
                  <div>{formatAmount(record.totalExpense)}</div>
                </div>
                <div className="service_item">
                  <div>Expense Log Items</div>
                  <div>{safeArray(record.expenseLogs).length}</div>
                </div>
                <div className="service_item">
                  <div>Attachments</div>
                  <div>{attachmentCount}</div>
                </div>
                <div className="service_item">
                  <div>Trip Status</div>
                  <div>{humanizeText(tripContext?.status)}</div>
                </div>
              </div>

              <LogsComments
                key={tripContextId || id || "driver-expense-history"}
                historyItems={historyItems}
                onFetchHistory={fetchHistory}
                onSubmitComment={submitComment}
                loading={historyLoading}
                emptyMessages={{
                  all: "No comments or activity found",
                  comment: "No comments found",
                  logs: "No activity found",
                }}
              />

              <div className="service_cost_summary_details rfq_right_card driver_details_notes_card driver_expense_notes_card">
                <div className="parts_general_notes">
                  <h5>General Notes</h5>
                  <p>Quick context and identifiers for this expense record.</p>

                  <div className="driver_expense_notes_list">
                    {generalNotes.map((note, index) => (
                      <div key={`expense-note-${index + 1}`} className="driver_expense_note_item">
                        {note}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </div>

      <CustomModal
        show={Boolean(previewAttachment)}
        handleClose={() => setPreviewAttachment(null)}
        heading="Attachments"
        dialogClassName="driver_expense_attachment_modal"
      >
        <div className="driver_expense_attachment_preview">
          <img
            src={previewAttachment?.preview}
            alt={previewAttachment?.name || "Expense attachment"}
          />
        </div>

        <div className="driver_expense_attachment_footer">
          <button
            type="button"
            className="driver_expense_download_btn"
            onClick={() => {
              if (!previewAttachment?.preview) return;
              const anchor = document.createElement("a");
              anchor.href = previewAttachment.preview;
              anchor.download = previewAttachment.name || "attachment";
              anchor.click();
            }}
          >
            Download
          </button>
          <button
            type="button"
            className="cancel_btn"
            onClick={() => setPreviewAttachment(null)}
          >
            Close
          </button>
        </div>
      </CustomModal>
    </div>
  );
};

export default DriverExpenseDetails;
